import { Badge } from "@/components/ui/badge";
import { Cpu, ShieldCheck, GitMerge, Link2, AlertCircle, CircleSlash, Type, ScanSearch, Hash, Tag, Truck, Factory, StickyNote, Boxes, Layers } from "lucide-react";

const STYLE = {
  part_exact: { cls: "bg-emerald-700 text-white border-transparent", Icon: Hash },
  title_exact: { cls: "bg-slate-900 text-white border-transparent", Icon: Type },
  title_word: { cls: "bg-slate-800 text-white border-transparent", Icon: Type },
  title_contains: { cls: "bg-slate-600 text-white border-transparent", Icon: Type },
  exact: { cls: "bg-emerald-600 text-white border-transparent", Icon: ShieldCheck },
  supersession: { cls: "bg-blue-600 text-white border-transparent", Icon: GitMerge },
  verified_interchange: { cls: "bg-teal-600 text-white border-transparent", Icon: Link2 },
  conditional: { cls: "bg-amber-500 text-white border-transparent", Icon: AlertCircle },
  unverified: { cls: "bg-red-50 text-[#B01E28] border border-dashed border-[#B01E28]", Icon: CircleSlash },
  partial: { cls: "bg-slate-200 text-slate-700 border border-slate-300", Icon: ScanSearch },
  category: { cls: "bg-slate-100 text-slate-600 border border-slate-200", Icon: Tag },
  brand: { cls: "bg-slate-100 text-slate-600 border border-slate-200", Icon: Factory },
  vehicle: { cls: "bg-slate-100 text-slate-600 border border-slate-200", Icon: Truck },
  keyword: { cls: "bg-amber-50 text-amber-700 border border-amber-200", Icon: Tag },
  notes: { cls: "bg-amber-100 text-amber-800 border border-amber-300", Icon: StickyNote },
  text: { cls: "bg-slate-100 text-slate-500 border border-slate-200", Icon: Type },
  dataplate: { cls: "bg-slate-900 text-white border-transparent", Icon: Cpu },
  words: { cls: "bg-slate-700 text-white border-transparent", Icon: Layers },
  tag_name: { cls: "bg-amber-50 text-amber-800 border border-amber-300", Icon: Tag },
  truck: { cls: "bg-sky-50 text-sky-800 border border-sky-300", Icon: Truck },
  lot: { cls: "bg-sky-50 text-sky-800 border border-sky-300", Icon: Boxes },
};

const KIND_WORD = { name: "name", brand: "brand", keyword: "keyword", tag_name: "tag name", category: "category",
  vehicle: "make / model", number: "part number", dataplate: "dataplate", extra: "labelled value", notes: "notes", code: "tag" };

export function MatchBadge({ match }) {
  if (!match) return null;
  const s = STYLE[match.type] || STYLE.text;
  const { Icon } = s;
  return (
    <Badge data-testid={`match-badge-${match.type}`} className={`rounded-md text-[9px] uppercase font-mono tracking-wide ${s.cls}`}>
      <Icon className="w-3 h-3 mr-1" />{match.label}
    </Badge>
  );
}

// Found only through one of the crew's approved synonyms — the row says which (Search S1).
export function SynonymNote({ match }) {
  const v = match?.via_synonym;
  if (!v) return null;
  return (
    <div data-testid="match-via-synonym" className="mt-1 text-[11px] text-slate-600">
      Found through the synonym <b className="text-slate-800">{v.typed}</b> = <b className="text-slate-800">{v.as}</b>
    </div>
  );
}

export function MatchDetail({ match }) {
  if (!match) return null;
  return <><SynonymNote match={match} /><MatchDetailBody match={match} /></>;
}

function Qualifier({ match }) {
  return (
    <div data-testid="match-qualifier" className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
      {match.matched_brand && <span className="font-medium text-slate-600">{match.matched_brand}</span>}
      {match.matched_owner && match.matched_owner !== "unknown" && <span className="text-slate-400">· {String(match.matched_owner).replace("_", " ")}</span>}
      <span className="text-slate-400">· {match.matched_source_label}</span>
      {match.ocr_confidence && <span className="text-slate-400">· OCR {match.ocr_confidence}</span>}
    </div>
  );
}

function MatchDetailBody({ match }) {
  // Fully-qualified line: whose number matched + how solid it is (source/OCR).
  const qualifier = match.matched_source_label && <Qualifier match={match} />;
  if (match.type === "supersession" && match.chain?.length) {
    return (
      <div data-testid="match-chain" className="flex flex-wrap items-center gap-1 mt-1 text-[11px] font-mono text-slate-500">
        <span className="uppercase text-[9px] text-slate-400 mr-1">Chain</span>
        {match.chain.map((c, i) => (
          <span key={i} className="flex items-center gap-1">
            <span className={`px-1.5 py-0.5 rounded ${i === match.chain.length - 1 ? "bg-blue-50 text-blue-700 font-bold" : "bg-slate-100"}`}>{c}</span>
            {i < match.chain.length - 1 && <span className="text-slate-400">→</span>}
          </span>
        ))}
      </div>
    );
  }
  if (match.type === "conditional" && match.condition) {
    return <div data-testid="match-condition" className="mt-1 text-[11px] text-amber-700"><b>Condition:</b> {match.condition}</div>;
  }
  if (match.type === "unverified") {
    return <>{qualifier}<div data-testid="match-unverified-note" className="mt-1 text-[11px] text-[#B01E28]">Unconfirmed AI suggestion — verify before relying on this.</div></>;
  }
  if (match.type === "dataplate") {
    const plates = match.matched_plates || [];
    return (
      <div data-testid="match-dataplate-note" className="mt-1 text-[11px] text-slate-600">
        <b className="font-mono">{match.number_matched}</b> is this engine's <b>{match.matched_label}</b>
        {plates.length ? <> — off the <b>{plates.join(" and ")}</b> dataplate{plates.length > 1 ? "s" : ""}</> : null}
        {match.matched_serial ? <span className="text-slate-400 font-mono"> · ESN {match.matched_serial}</span> : null}
      </div>
    );
  }
  if (match.type === "truck") {
    return (
      <div data-testid="match-truck-note" className="mt-1 text-[11px] text-slate-600">
        From its truck <b className="font-mono">{match.truck_stock_number || ""}</b> — its {match.matched_in}
      </div>
    );
  }
  if (match.type === "lot") return <div data-testid="match-lot-note" className="mt-1 text-[11px] text-slate-600">From its lot — the lot number</div>;
  if (match.type === "words" && Array.isArray(match.matched_in)) {
    return <div data-testid="match-words-note" className="mt-1 text-[11px] text-slate-600">Matched in its {match.matched_in.map((k) => KIND_WORD[k] || k).join(" + ")}</div>;
  }
  if (match.type === "partial") {
    const more = match.terms_total > match.terms_covered ? match.terms_total - match.terms_covered : 0;
    return <>{qualifier}<div data-testid="match-partial-note" className="mt-1 text-[11px] text-slate-500">Partial number match{match.number_matched ? ` — contains in ${match.matched_in || "number"}: ` : ""}{match.number_matched && <b className="font-mono">{match.number_matched}</b>}{more ? <span className="text-slate-400"> · +{more} more field{more === 1 ? "" : "s"} matched</span> : null}</div></>;
  }
  return qualifier || null;
}

// ONE PLAIN "Matched: …" LINE on every part and truck row (Search S3, G16) — instead of a badge on one screen and a
// reason line on another. What the line can't say in a few words (a supersession chain, an interchange condition,
// the red AI-suggestion warning, the synonym it came through) still follows underneath.
const M = ({ children }) => <b className="font-mono text-slate-900">{children}</b>;
const NUM_WORD = { OEM: "part number", numbers: "part number", superseded: "old number", interchange: "interchange number" };

// S5 — HOW the typed number sat in the value: exact needs no words; the rest say where in it.
function HowNote({ m }) {
  if (!m?.how || !m.piece || m.how === "exact") return null;
  const p = <M>{m.piece}</M>;
  if (m.code_in && m.code_value) {
    // the line above named where the OTHER words sat; this names the value the code itself sat in
    const tail = { starts: <>starts with {p}</>, whole: <>has {p} as a whole piece</>, ends: <>ends with {p}</>,
      contains: <>has {p} inside it</> }[m.how];
    return tail ? <span data-testid="match-how" data-how={m.how} className="text-slate-500"> — its {NUM_WORD[m.code_in] || m.code_in} <M>{m.code_value}</M> {tail}</span> : null;
  }
  const txt = { starts: <>starts with {p}</>, whole: <>{p} is a whole piece of it</>,
    ends: <>ends with {p}</>, contains: <>{p} is inside it</>,
    pattern: <>fits {p}</> }[m.how];
  return txt ? <span data-testid="match-how" data-how={m.how} className="text-slate-500"> — {txt}</span> : null;
}

function partPhrase(m) {
  const n = m.number_matched;
  if (m.how && n && m.matched_in && !["truck", "lot", "words"].includes(m.type)) {
    return <>its {NUM_WORD[m.matched_in] || m.matched_in} <M>{n}</M></>;
  }
  switch (m.type) {
    case "part_exact": return <>its tag <M>{n}</M></>;
    case "title_exact": return "the name, word for word";
    case "title_word": return "a word in the name";
    case "title_contains": return "part of the name";
    case "exact": return <>part number <M>{n}</M></>;
    case "supersession": return <>an old number, <M>{n}</M></>;
    case "verified_interchange": return <>verified interchange <M>{n}</M></>;
    case "conditional": return <>interchange <M>{n}</M>, with a condition</>;
    case "unverified": return <>AI-suggested interchange <M>{n}</M></>;
    case "partial": return n ? <>part of its {m.matched_in || "number"} <M>{n}</M></> : "part of a number";
    case "brand": return "its brand";
    case "vehicle": return "its make / model";
    case "category": return "its category";
    case "keyword": return "a keyword";
    case "extra": return <>a labelled value{m.matched_label ? ` (${m.matched_label})` : ""} <M>{n}</M></>;
    case "dataplate": {
      const plates = m.matched_plates || [];
      return <span data-testid="match-dataplate-note"><M>{n}</M> — this engine's {m.matched_label}{plates.length ? ` (off the ${plates.join(" and ")} dataplate${plates.length > 1 ? "s" : ""})` : ""}</span>;
    }
    case "notes": return "its notes";
    case "tag_name": return "a tag name";
    case "words": return <span data-testid="match-words-note">its {(Array.isArray(m.matched_in) ? m.matched_in : [m.matched_in]).map((k) => KIND_WORD[k] || k).join(" + ")}</span>;
    case "truck": return <span data-testid="match-truck-note">from its truck <M>{m.truck_stock_number || ""}</M> — its {m.matched_in}</span>;
    case "lot": return <span data-testid="match-lot-note">from its lot — the lot number</span>;
    default: return "a value on it";
  }
}

function truckPhrase(m) {
  const n = m.number_matched;
  const label = String(m.label || "").toLowerCase().replace(/ match$/, "");
  if (m.type === "dataplate") return <><M>{n}</M> — its {m.matched_label || "dataplate"}</>;
  if (m.matched_in && n) return <>its {String(m.matched_in).toLowerCase() === "vin" ? "VIN" : m.matched_in} <M>{n}</M></>;
  if (m.matched_in) return <>it has {m.matched_in} <span className="text-slate-500">— your word matched the spec's name, not a value</span></>;
  if (label === "stock #") return "its stock number";
  if (label && label !== "match") return `its ${label}`;
  return "a value on it";
}

export function MatchedLine({ match, kind = "part", testid = "matched-line" }) {
  if (!match) return null;
  const detail = ["supersession", "conditional", "unverified"].includes(match.type);
  return (
    <div data-testid={testid} data-match-type={match.type} data-match-how={match.how || undefined} className="mt-1 text-sm leading-snug text-slate-600">
      <span className="font-semibold text-slate-800">Matched:</span> {kind === "truck" ? truckPhrase(match) : partPhrase(match)}<HowNote m={match} />
      <SynonymNote match={match} />
      {detail ? <MatchDetailBody match={match} /> : (kind === "part" && match.matched_source_label ? <Qualifier match={match} /> : null)}
    </div>
  );
}
